import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { getRetry } from "../lib/api";
import { useStore } from "../context/StoreContext";
import { CourseCard } from "../components/CourseCard";
import { Stars } from "../lib/art";

const DUR_RANGES = { short: [0, 3], med: [3, 7], long: [7, 12], xl: [12, Infinity] };
const SORTS = {
  rated: (a, b) => (b.rating || 0) - (a.rating || 0),
  newest: (a, b) => String(b.created_at).localeCompare(String(a.created_at)),
  priceLow: (a, b) => a.effective_price - b.effective_price,
  priceHigh: (a, b) => b.effective_price - a.effective_price,
  popular: (a, b) => (b.students_count || 0) - (a.students_count || 0),
};

export default function ResultsPage() {
  const [params] = useSearchParams();
  const { sections } = useStore();
  const [courses, setCourses] = useState([]);
  const [status, setStatus] = useState("loading");
  const [F, setF] = useState({ rating: 0, levels: params.get("level") ? [params.get("level")] : [], price: "all", durations: [], sort: "popular" });

  const q = params.get("q") || "";
  const secId = params.get("section") || "";

  const loadCourses = () => {
    setStatus("loading");
    getRetry("/courses")
      .then((r) => { setCourses(r.data); setStatus("ok"); })
      .catch((e) => { console.warn("Courses load failed:", e?.message); setStatus("error"); });
  };
  useEffect(() => { loadCourses(); }, []);
  useEffect(() => { setF((f) => ({ ...f, levels: params.get("level") ? [params.get("level")] : [] })); }, [params]);

  const secMap = Object.fromEntries(sections.map((s) => [s.id, s]));
  const sec = secId ? secMap[secId] : null;

  const list = useMemo(() => {
    let l = courses.filter((c) => !secId || c.section === secId);
    if (q) {
      const ql = q.toLowerCase();
      l = l.filter((c) => c.title.toLowerCase().includes(ql) || (c.subtitle || "").toLowerCase().includes(ql) || (c.instructor || "").toLowerCase().includes(ql));
    }
    l = l.filter((c) => {
      if (F.rating && (c.rating || 0) < F.rating) return false;
      if (F.levels.length && !F.levels.includes(c.level)) return false;
      if (F.price === "free" && c.effective_price !== 0) return false;
      if (F.price === "paid" && c.effective_price === 0) return false;
      if (F.durations.length) {
        const h = (c.total_duration_min || 0) / 60;
        if (!F.durations.some((d) => { const [lo, hi] = DUR_RANGES[d]; return h >= lo && h < hi; })) return false;
      }
      return true;
    });
    l.sort(SORTS[F.sort] || SORTS.popular);
    return l;
  }, [courses, q, secId, F]);

  const set = (k, v) => setF({ ...F, [k]: v });
  const tog = (k, v) => setF({ ...F, [k]: F[k].includes(v) ? F[k].filter((x) => x !== v) : [...F[k], v] });

  const FGroup = ({ title, children }) => {
    const [open, setOpen] = useState(true);
    return (
      <div className={`f-group ${open ? "open" : ""}`}>
        <button className="f-head" onClick={() => setOpen(!open)}>{title}<span className="pm">+</span></button>
        <div className="f-body"><div>{children}</div></div>
      </div>
    );
  };

  let heading = "All Courses";
  if (sec) heading = `${sec.icon} ${sec.title}`;
  else if (q) heading = `Results for “${q}”`;

  return (
    <div className="wrap">
      <div className="results-head">
        <h1 data-testid="results-title">{heading}</h1>
        <p>{sec ? sec.tagline : "Every course on Unreal Academy"}</p>
      </div>
      <div className="results-layout">
        <aside className="filters" data-testid="filters-sidebar">
          <FGroup title="Ratings">
            {[4.5, 4.0, 3.5, 3.0].map((r) => (
              <label className="f-opt" key={r}>
                <input type="radio" name="fr" checked={F.rating === r} onChange={() => set("rating", r)} data-testid={`filter-rating-${r}`} />
                <Stars r={r} /> {r} & up
              </label>
            ))}
            <button className="clear-f" onClick={() => set("rating", 0)}>Clear rating</button>
          </FGroup>
          <FGroup title="Level">
            {["Beginner", "Beginner–Intermediate", "Intermediate", "Advanced"].map((l) => (
              <label className="f-opt" key={l}>
                <input type="checkbox" checked={F.levels.includes(l)} onChange={() => tog("levels", l)} data-testid={`filter-level-${l.toLowerCase().replace(/[^a-z]+/g, "-")}`} /> {l}
              </label>
            ))}
          </FGroup>
          <FGroup title="Video Duration">
            {[["short", "0–3 hours"], ["med", "3–7 hours"], ["long", "7–12 hours"], ["xl", "12+ hours"]].map(([k, l]) => (
              <label className="f-opt" key={k}>
                <input type="checkbox" checked={F.durations.includes(k)} onChange={() => tog("durations", k)} data-testid={`filter-duration-${k}`} /> {l}
              </label>
            ))}
          </FGroup>
          <FGroup title="Price">
            {[["all", "All"], ["free", "Free"], ["paid", "Paid"]].map(([v, l]) => (
              <label className="f-opt" key={v}>
                <input type="radio" name="fp" checked={F.price === v} onChange={() => set("price", v)} data-testid={`filter-price-${v}`} /> {l}
              </label>
            ))}
          </FGroup>
        </aside>
        <div>
          <div className="sort-row">
            <span className="n" data-testid="results-count">{list.length} result{list.length !== 1 ? "s" : ""}</span>
            <select className="sort-sel" data-testid="sort-select" value={F.sort} onChange={(e) => set("sort", e.target.value)}>
              <option value="popular">Most Popular</option>
              <option value="rated">Highest Rated</option>
              <option value="newest">Newest</option>
              <option value="priceLow">Price: Low to High</option>
              <option value="priceHigh">Price: High to Low</option>
            </select>
          </div>
          <div className="results-grid" data-testid="course-grid">
            {status === "error" && (
              <div className="empty" data-testid="catalog-load-error">
                <p>Couldn't load courses — please check your connection.</p>
                <button className="btn-primary mt-4" data-testid="catalog-retry-btn" onClick={loadCourses}>RETRY</button>
              </div>
            )}
            {status === "loading" && [1, 2, 3].map((i) => <div key={i} className="skel-card" />)}
            {status === "ok" && (list.length ? list.map((c) => <CourseCard key={c.id} c={c} secMap={secMap} />)
              : <div className="empty" data-testid="no-results">No courses match your filters. Try clearing some.</div>)}
          </div>
        </div>
      </div>
    </div>
  );
}
